/**
 * Type picker for creating a session.
 */
import * as React from 'react';
import { CalendarPlus } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { cn } from '@/lib/utils';
import {
  getVisibleInPersonCreateTypeOptions,
  type SessionInPersonCreateTypeOption,
} from '@/components/sessions/session-configs';
import { groupInPersonCreateTypeOptions } from '@/components/sessions/inperson/session-form-utils';
import type { PlanificationType, SessionTab } from '@/types/inperson/session.types';

/** Props for {@link SelectSessionTypeModal}. */
interface SelectSessionTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (type: PlanificationType) => void;
  /** RBAC-gated tabs — if provided, only show options for allowed tabs. */
  allowedTabIds?: SessionTab[];
}

/** Type picker for creating a session. */
export const SelectSessionTypeModal: React.FC<Readonly<SelectSessionTypeModalProps>> = ({
  open,
  onClose,
  onSelect,
  allowedTabIds,
}) => {
  const { tutoring: tutoringOptions, information: informationOptions } =
    groupInPersonCreateTypeOptions(getVisibleInPersonCreateTypeOptions(allowedTabIds));

  /** One planification-type card — click selects the type and closes via the parent. */
  const renderCard = (option: SessionInPersonCreateTypeOption) => {
    const Icon = option.icon;
    return (
      <button
        key={option.id}
        type="button"
        onClick={() => onSelect(option.id)}
        className={cn(
          'group relative flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all w-full',
          'border-[var(--color-1-nuance-3)] bg-[color-mix(in_srgb,var(--color-1-nuance-3)_3%,transparent)]',
          'hover:-translate-y-0.5 hover:shadow-lg hover:border-[var(--type-accent)]',
          'hover:bg-[color-mix(in_srgb,var(--type-accent)_8%,transparent)]',
          'focus:outline-none focus-visible:ring-2',
        )}
        style={{ '--type-accent': option.accentVar } as React.CSSProperties}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[var(--type-accent)] bg-[color-mix(in_srgb,var(--type-accent)_10%,transparent)] transition-all">
          <Icon className="h-6 w-6 text-[var(--type-accent)]" />
        </div>
        <div>
          <h3 className="text-base font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
            {option.title}
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {option.description}
          </p>
        </div>
      </button>
    );
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="lg"
      header={{
        icon: CalendarPlus,
        iconSize: 'md',
        title: 'Créer une séance',
        subtitle: 'Sélectionnez le type de séance à créer.',
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      hideContentWrapper
    >
      <div className="flex flex-col gap-4 mt-2">
        {tutoringOptions.length > 0 && (
          <div className="flex flex-col gap-2">
            <p
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--color-1-nuance-2)' }}
            >
              Tutorat
            </p>
            <div className="grid grid-cols-2 gap-3">{tutoringOptions.map(renderCard)}</div>
          </div>
        )}
        {informationOptions.length > 0 && (
          <div className="flex flex-col gap-2">
            <p
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--color-1-nuance-2)' }}
            >
              Réunion d&apos;information
            </p>
            <div className="grid grid-cols-1">{informationOptions.map(renderCard)}</div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};
